
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
export const TAB = '    ';

export const getPathSegments = (path) => (path ? path.split('.').filter(Boolean) : []);

export const buildAccessorFromSegments = (segments, base = 'json_value') => {
    if (!segments.length) {
        return base;
    }
    return segments.reduce((acc, segment) => `${acc}.get("${segment}")`, base);
};

export const buildJsonAccessor = (path, defaultValue = '""') => {
    if (!path) return '';
    const segments = getPathSegments(path);
    if (!segments.length) return defaultValue;
    return segments.map(segment => `.get("${segment}", ${defaultValue})`).join('');
};

export const buildAccessorWithBase = (path, base = 'json_value') => buildAccessorFromSegments(getPathSegments(path), base);

// Like ``buildAccessorFromSegments`` but every ``.get`` carries a default so a
// missing key never raises (intermediate levels default to ``{}`` so chaining
// stays safe; the leaf uses ``defaultValue``).
export const buildAccessorFromSegmentsWithDefault = (segments, base, defaultValue) => {
    if (!segments.length) {
        return base;
    }
    return segments.reduce((acc, segment, index) => {
        const fallback = index === segments.length - 1 ? defaultValue : '{}';
        return `${acc}.get("${segment}", ${fallback})`;
    }, base);
};

export const buildRelativeAccessor = (path, ancestorPath, base = 'record') => {
    const pathSegments = getPathSegments(path);
    const ancestorSegments = getPathSegments(ancestorPath);
    if (ancestorSegments.length > pathSegments.length) {
        return null;
    }
    for (let i = 0; i < ancestorSegments.length; i++) {
        if (pathSegments[i] !== ancestorSegments[i]) {
            return null;
        }
    }
    const relativeSegments = pathSegments.slice(ancestorSegments.length);
    return buildAccessorFromSegments(relativeSegments, base);
};

export const buildRelativeAccessorWithDefault = (path, ancestorPath, base, defaultValue) => {
    const pathSegments = getPathSegments(path);
    const ancestorSegments = getPathSegments(ancestorPath);
    if (ancestorSegments.length > pathSegments.length) {
        return null;
    }
    for (let i = 0; i < ancestorSegments.length; i++) {
        if (pathSegments[i] !== ancestorSegments[i]) {
            return null;
        }
    }
    const relativeSegments = pathSegments.slice(ancestorSegments.length);
    return buildAccessorFromSegmentsWithDefault(relativeSegments, base, defaultValue);
};

export const combineArrayPathAndKey = (arrayPath, key) => {
    if (!arrayPath) {
        return key;
    }
    return key ? `${arrayPath}.${key}` : arrayPath;
};

export const accentPathFromAccessor = (accessor) => {
    if (!accessor) return null;
    // Match .get("key") style access, possibly with a default value second argument
    // Regex matches .get("key", default) or .get("key")
    const matches = [...accessor.matchAll(/\.get\("([^"]+)"(?:,\s*[^)]+)?\)/g)].map((m) => m[1]);
    if (!matches.length) {
        // Fallback to ["key"] style just in case
        const bracketMatches = [...accessor.matchAll(/\["([^"]+)"\]/g)].map((m) => m[1]);
        if (!bracketMatches.length) return null;
        return bracketMatches.join('.');
    }
    return matches.join('.');
};

export const deriveParserSelectionsFromCode = (code, taskType, isMultilabel) => {
    if (!code || !taskType) {
        return null;
    }
    const selections = {};

    const findLoop = (codeStr, collectionName) => {
        // 1. Find the append call
        const appendRegex = new RegExp(`${collectionName}\\.append`, 'g');
        const appendMatch = appendRegex.exec(codeStr);
        if (!appendMatch) return null;

        const appendIndex = appendMatch.index;
        const codeBeforeAppend = codeStr.slice(0, appendIndex);

        // 2. Find the last 'for' loop in the code before the append
        // Matches:
        // for <var> in json_value["<key>"]:
        // for <var> in json_value.get("<key>"):
        // for <var> in json_value.get("<key>", []):
        // for <var> in json_value:
        const loopRegex = /for\s+(\w+)\s+in\s+json_value(?:(?:\[["']([^"']+)["']\])|(?:\.get\(\s*["']([^"']+)["'](?:,\s*[^)]+)?\s*\)))?\s*:/g;

        let lastLoopMatch = null;
        let match;
        while ((match = loopRegex.exec(codeBeforeAppend)) !== null) {
            // Normalize the match result so index 2 always holds the key if present
            // match[1] = loopVar
            // match[2] = key from ["key"]
            // match[3] = key from .get("key")
            const key = match[2] || match[3];
            // Reconstruct a standard match object compatible with existing consumers: [fullMatch, loopVar, key]
            lastLoopMatch = [match[0], match[1], key];
        }

        return lastLoopMatch;
    };

    const findAssignment = (block, keyName, varName) => {
        // Matches "key": <var>["path"] or "key": int(<var>["path"]) or 'key': ...
        // More robust: allows for extra spaces, newlines, optional trailing comma, single/double quotes
        // Look for the specific pattern used in our generator:
        // "key": var["path"] or "key": var.get("path")
        // Update: allow space after int, e.g. int ( var[...] )

        // Try matching .get() style first
        const regexGet = new RegExp(`["']${keyName}["']\\s*:\\s*(?:int\\s*\\()?\\s*${varName}\\.get\\(["']([^"']+)["'](?:,\\s*[^)]+)?\\)`, 'm');
        let match = regexGet.exec(block);
        if (match) return match[1];

        // Fallback to bracket style
        const regexBracket = new RegExp(`["']${keyName}["']\\s*:\\s*(?:int\\s*\\()?\\s*${varName}\\\[["']([^"']+)["']\\\]`, 'm');
        match = regexBracket.exec(block);
        return match ? match[1] : null;
    };

    const labelAccessorToPath = (match, type) => {
        if (!match || !match[1]) {
            return;
        }
        const fieldPath = accentPathFromAccessor(match[1]);
        if (fieldPath) {
            selections.classification = {
                type,
                fieldPath,
                // Legacy single-label parsers stringify the whole field value,
                // so a list-valued field means "entire list" mode.
                ...(type === 'single' ? { listMode: 'full' } : {}),
            };
        }
    };

    if (taskType === TaskType.classification) {
        // Check for text mode pattern
        // 1. Single label pattern: "labels": [(llm_output or "").strip()]
        // 2. Multilabel list pattern: labels = json.loads(llm_output) ... labels = [str(l).strip() for l in labels]
        // 3. Multilabel split pattern: "labels": [l.strip() for l in (llm_output or "").split(delimiter) if l.strip()]

        const singleLabelTextRegex = /"labels"\s*:\s*\[\(llm_output\s+or\s+""\)\.strip\(\)\]/;
        const multilabelListRegex = /labels\s*=\s*json\.loads\(llm_output\)[\s\S]*?"labels"\s*:\s*\[str\(l\)\.strip\(\)\s+for\s+l\s+in\s+labels\]/;
        const multilabelListSingleLineRegex = /"labels"\s*:\s*\[str\(l\)\.strip\(\)\s+for\s+l\s+in\s+json\.loads\(llm_output\)\]/;
        const multilabelSplitRegex = /"labels"\s*:\s*\[l\.strip\(\)\s+for\s+l\s+in\s+\(llm_output\s+or\s+""\)\.split\(delimiter\)\s+if\s+l\.strip\(\)\]/;

        if (singleLabelTextRegex.test(code)) {
            selections.classification = {
                type: 'text',
                fieldPath: null,
            };
            return selections;
        }

        if (multilabelListRegex.test(code) || multilabelListSingleLineRegex.test(code)) {
            selections.classification = {
                type: 'text',
                fieldPath: null,
                delimiter: 'list',
                isMultilabel: true
            };
            return selections;
        }

        const splitMatch = multilabelSplitRegex.exec(code);
        if (splitMatch) {
            // Try to extract delimiter
            const delimiterMatch = /delimiter\s*=\s*["']([^"']+)["']/.exec(code);
            const delimiter = delimiterMatch ? delimiterMatch[1] : null;

            selections.classification = {
                type: 'text',
                fieldPath: null,
                delimiter: delimiter,
                isMultilabel: true
            };
            return selections;
        }

        // Single-label first-item pattern (list-valued field, label = first
        // item):
        //   label_value = json_value.get("labels", "")
        //   if isinstance(label_value, list):
        //       label_value = label_value[0] if label_value else ""
        //   ... "labels": [str(label_value).strip()]
        const firstItemRegex = /label_value\s*=\s*json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)[\s\S]*?isinstance\(label_value,\s*list\)[\s\S]*?["']labels["']\s*:\s*\[\s*str\(label_value\)\.strip\(\)\s*\]/;
        const firstItemMatch = firstItemRegex.exec(code);
        if (firstItemMatch) {
            const fieldPath = accentPathFromAccessor(firstItemMatch[1]);
            if (fieldPath) {
                selections.classification = {
                    type: 'single',
                    fieldPath,
                    listMode: 'first',
                };
                return selections;
            }
        }

        // Updated regexes to handle single/double quotes and .get()
        const collectionRegex = /for\s+(\w+)\s+in\s+json_value(?:\[["']([^"']+)["']\]|\.get\(["']([^"']+)["']\))?\s*:\s*labels\.append\(\{\s*["']label["']\s*:\s*\1(?:\[["']([^"']+)["']\]|\.get\(["']([^"']+)["']\))/;
        const labelsRegex = /["']labels["']\s*:\s*json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)/;
        const labelRegex = /["']label["']\s*:\s*json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)/;
        // JSON multi-label with list delimiter:
        // "labels": [a.strip() for a in json_value.get("result", [])]
        const jsonListComprehensionRegex = /["']labels["']\s*:\s*\[a\.strip\(\)\s+for\s+a\s+in\s+json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)\s*\]/;

        const jsonListMatch = jsonListComprehensionRegex.exec(code);
        if (jsonListMatch) {
            selections.classification = {
                type: 'multi',
                fieldPath: accentPathFromAccessor(jsonListMatch[1]),
                delimiter: 'list',
            };
            return selections;
        }

        const collectionMatch = collectionRegex.exec(code);
        if (collectionMatch) {
            const [, loopVar, arrayKeyBracket, arrayKeyGet, fieldKeyBracket, fieldKeyGet] = collectionMatch;
            const arrayKey = arrayKeyBracket || arrayKeyGet;
            const fieldKey = fieldKeyBracket || fieldKeyGet;
            const arrayPath = arrayKey === undefined ? '' : arrayKey;
            const fieldPath = combineArrayPathAndKey(arrayKey, fieldKey);
            selections.classification = {
                type: 'multi',
                fieldPath,
                arrayPath: arrayPath,
            };
        } else {
            // Try to match the explicit split pattern: "labels": [a.strip() for a in json_value["value"].split(delimiter)]
            // Or literal delimiter: ...split(",")
            // Matches accessor like ["value"] or ["data"]["value"] or .get("value")
            // Also supports wrapping in str(), e.g. str(json_value... ).split(delimiter)
            const splitRegex = /["']labels["']\s*:\s*\[a\.strip\(\)\s+for\s+a\s+in\s+(?:str\(\s*json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)\s*\)|json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+))\.split\(([^)]+)\)\]/;
            const splitMatch = splitRegex.exec(code);

            if (splitMatch) {
                const accessor = splitMatch[1] || splitMatch[2];
                const delimiterArg = splitMatch[3];
                // delimiterArg might be 'delimiter' variable or literal string
                let delimiter = null;
                const delimiterVarMatch = /delimiter\s*=\s*["']([^"']+)["']/.exec(code);
                if (delimiterVarMatch) {
                    delimiter = delimiterVarMatch[1];
                } else if (delimiterArg.startsWith('"') || delimiterArg.startsWith("'")) {
                    delimiter = delimiterArg.slice(1, -1);
                }

                selections.classification = {
                    type: 'multi',
                    fieldPath: accentPathFromAccessor(accessor),
                    delimiter: delimiter
                };
            } else {
                // Try to match the single label pattern specifically first
                // "labels": [str(json_value.get("intent_string", "")).strip()]
                // regex to match: "labels": [str(json_value...)]
                const singleLabelRegex = /["']labels["']\s*:\s*\[\s*str\(\s*json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)\s*\)\.strip\(\)\s*\]/;
                const match = singleLabelRegex.exec(code);
                if (match) {
                    labelAccessorToPath(match, 'single');
                } else {
                    if (isMultilabel) {
                        labelAccessorToPath(labelsRegex.exec(code) || labelRegex.exec(code), 'multi');
                    } else {
                        labelAccessorToPath(labelRegex.exec(code) || labelsRegex.exec(code), 'single');
                    }
                }
            }
        }
    }

    if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
        // Try to find entity loop
        const entityLoopMatch = findLoop(code, 'entities');
        if (entityLoopMatch) {
            const [, loopVar, arrayKey] = entityLoopMatch;
            const arrayPath = arrayKey === undefined ? '' : arrayKey;
            // Extract the block for entities
            // Heuristic: search for text between 'entities.append({' and '})'
            const entityBlockRegex = /entities\.append\(\{([\s\S]*?)\}\)/;
            const entityBlockMatch = entityBlockRegex.exec(code);

            if (entityBlockMatch) {
                const block = entityBlockMatch[1];
                const labelKey = findAssignment(block, 'label', loopVar);
                const contentKey = findAssignment(block, 'content', loopVar);
                const startKey = findAssignment(block, 'start_index', loopVar);

                if (labelKey && contentKey && startKey) {
                    selections.ner = {
                        labelPath: combineArrayPathAndKey(arrayKey, labelKey),
                        labelArrayPath: arrayPath,
                        contentPath: combineArrayPathAndKey(arrayKey, contentKey),
                        contentArrayPath: arrayPath,
                        startIndexPath: combineArrayPathAndKey(arrayKey, startKey),
                        startIndexArrayPath: arrayPath,
                    };
                }
            }
        } else {
            // Try direct return (list of entities)
            const entityDirectRegex = /return\s*\[\s*\{\s*["']label["']:\s*json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)[\s\S]*?["']content["']:\s*json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)[\s\S]*?["']start_index["']:\s*int\(json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)\)[\s\S]*?\}\s*\]/m;
            const entityDirectMatch = entityDirectRegex.exec(code);
            if (entityDirectMatch) {
                const [, labelAccessor, contentAccessor, startAccessor] = entityDirectMatch;
                selections.ner = {
                    labelPath: accentPathFromAccessor(labelAccessor),
                    contentPath: accentPathFromAccessor(contentAccessor),
                    startIndexPath: accentPathFromAccessor(startAccessor),
                };
            }
        }
    }

    if (taskType === TaskType.NER_WITH_RELATIONS) {
        const relationLoopMatch = findLoop(code, 'relations');
        if (relationLoopMatch) {
            const [, loopVar, arrayKey] = relationLoopMatch;
            const arrayPath = arrayKey === undefined ? '' : arrayKey;
            const relationBlockRegex = /relations\.append\(\{([\s\S]*?)\}\)/;
            const relationBlockMatch = relationBlockRegex.exec(code);

            if (relationBlockMatch) {
                const block = relationBlockMatch[1];
                const labelKey = findAssignment(block, 'label', loopVar);
                const sourceKey = findAssignment(block, 'source_start_index', loopVar);
                const targetKey = findAssignment(block, 'target_start_index', loopVar);

                if (labelKey && sourceKey && targetKey) {
                    selections.relations = {
                        labelPath: combineArrayPathAndKey(arrayKey, labelKey),
                        labelArrayPath: arrayPath,
                        sourceIndexPath: combineArrayPathAndKey(arrayKey, sourceKey),
                        sourceArrayPath: arrayPath,
                        targetIndexPath: combineArrayPathAndKey(arrayKey, targetKey),
                        targetArrayPath: arrayPath,
                    };
                }
            }
        }
    }

    if (isGenerativeTask(taskType)) {
        // Check for text mode pattern: "response": (llm_output or "").strip()
        const textResponseRegex = /"response"\s*:\s*\(llm_output\s+or\s+""\)\.strip\(\)/;
        if (textResponseRegex.test(code)) {
            selections.summarization = {
                type: 'text',
                fieldPath: null,
            };
            return selections;
        }

        // Content generation only: entire-response JSON mode pattern,
        // "response": llm_output_clean (the whole fence-stripped output).
        if (taskType === TaskType.contentGeneration) {
            const entireResponseRegex = /"response"\s*:\s*llm_output_clean/;
            if (entireResponseRegex.test(code)) {
                selections.summarization = {
                    type: 'json',
                    fieldPath: null,
                };
                return selections;
            }
        }

        // Check for JSON mode pattern: "response": str(json_value.get("field", "")).strip()
        const jsonResponseRegex = /"response"\s*:\s*str\(json_value((?:(?:\[[^\]]+\])|(?:\.get\([^)]+\)))+)\)\.strip\(\)/;
        const jsonMatch = jsonResponseRegex.exec(code);
        if (jsonMatch) {
            const fieldPath = accentPathFromAccessor(jsonMatch[1]);
            if (fieldPath) {
                selections.summarization = {
                    type: 'json',
                    fieldPath,
                };
                return selections;
            }
        }
    }

    return Object.keys(selections).length ? selections : null;
};

export const getCommonArrayPath = (...paths) => {
    const filtered = paths.filter((path) => path !== null && path !== undefined);
    if (!filtered.length) {
        return null;
    }
    const first = filtered[0];
    return filtered.every(path => path === first) ? first : null;
};

const JSON_FENCE_STRIP_LINES = [
    'if "```json" in llm_output:',
    '    llm_output_clean = llm_output.split("```json")[1].split("```")[0].strip()',
    'else:',
    '    llm_output_clean = llm_output.strip()',
];

export const createClassificationPrefillCode = (prefill) => {
    if (prefill?.type === 'text') {
        if (prefill.isMultilabel && prefill.delimiter) {
            if (prefill.delimiter === 'list') {
                return [
                    ...JSON_FENCE_STRIP_LINES,
                    'try:',
                    '    labels = json.loads(llm_output_clean)',
                    '    if not isinstance(labels, list):',
                    '        labels = [str(labels)]',
                    'except:',
                    '    labels = [llm_output_clean]',
                    '',
                    'return {',
                    '    "labels": [str(l).strip() for l in labels],',
                    '}',
                ].join('\n');
            }
            return [
                `delimiter = "${prefill.delimiter}"`,
                'return {',
                '    "labels": [l.strip() for l in (llm_output or "").split(delimiter) if l.strip()],',
                '}',
            ].join('\n');
        }

        return [
            'return {',
            '    "labels": [(llm_output or "").strip()],',
            '}',
        ].join('\n');
    }

    if (!prefill?.fieldPath) return null;
    // Use buildJsonAccessor for safe .get access
    const accessor = buildJsonAccessor(prefill.fieldPath, prefill.type === 'multi' && prefill.delimiter === 'list' ? '[]' : '""');
    if (!accessor) return null;

    // Classification multi-label with JSON response
    if (prefill.type === 'multi') {
        // Delimiter = List → treat field as an array and iterate over values directly
        if (prefill.delimiter === 'list') {
            return [
                ...JSON_FENCE_STRIP_LINES,
                'json_value = json.loads(llm_output_clean)',
                '',
                'return {',
                `    "labels": [a.strip() for a in json_value${accessor}],`,
                '}',
            ].join('\n');
        }

        // Delimiter is any non-list value → split stringified field value
        if (prefill.delimiter) {
            return [
                ...JSON_FENCE_STRIP_LINES,
                'json_value = json.loads(llm_output_clean)',
                '',
                `delimiter = "${prefill.delimiter}"`,
                'return {',
                `    "labels": [a.strip() for a in str(json_value${accessor}).split(delimiter)],`,
                '}',
            ].join('\n');
        }
    }

    // Classification single label, first-item mode: the field holds a list
    // (e.g. {"labels": ["harmful"]}) and the label is its first item. Robust
    // at runtime to scalar values and empty lists.
    if (prefill.listMode === 'first') {
        return [
            ...JSON_FENCE_STRIP_LINES,
            'json_value = json.loads(llm_output_clean)',
            '',
            `label_value = json_value${accessor}`,
            'if isinstance(label_value, list):',
            '    label_value = label_value[0] if label_value else ""',
            '',
            'return {',
            '    "labels": [str(label_value).strip()],',
            '}',
        ].join('\n');
    }

    // Classification single label (entire value — for a list-valued field
    // this is the stringified list, e.g. "['harmful']").
    return [
        ...JSON_FENCE_STRIP_LINES,
        'json_value = json.loads(llm_output_clean)',
        '',
        'return {',
        `    "labels": [str(json_value${accessor}).strip()],`,
        '}',
    ].join('\n');
};

export const createNerPrefillCode = (entityPaths) => {
    const labelAccessor = buildJsonAccessor(entityPaths.labelPath, '""');
    const contentAccessor = buildJsonAccessor(entityPaths.contentPath, '""');
    const startAccessor = buildJsonAccessor(entityPaths.startIndexPath, '0');
    if (!labelAccessor || !contentAccessor || !startAccessor) {
        return null;
    }

    const arrayPath = getCommonArrayPath(
        entityPaths.labelArrayPath,
        entityPaths.contentArrayPath,
        entityPaths.startIndexArrayPath,
    );

    if (arrayPath !== null) {
        const collectionAccessor = buildAccessorWithBase(arrayPath);
        const relativeLabel = buildRelativeAccessorWithDefault(entityPaths.labelPath, arrayPath, 'record', '""');
        const relativeContent = buildRelativeAccessorWithDefault(entityPaths.contentPath, arrayPath, 'record', '""');
        const relativeStart = buildRelativeAccessorWithDefault(entityPaths.startIndexPath, arrayPath, 'record', '0');
        if (!relativeLabel || !relativeContent || !relativeStart) {
            return null;
        }
        const lines = [
            ...JSON_FENCE_STRIP_LINES,
            'json_value = json.loads(llm_output_clean)',
            '',
        ];
        // Tolerate models that wrap the result in a top-level list. Each element
        // may either be a record holding the "<key>" array
        // (e.g. ``[{"example": ..., "entities": [...]}]``) or an entity object
        // itself (e.g. ``[{"label": ...}]``). Flatten both into a single dict so
        // ``json_value.get("<key>")`` works and we never iterate the wrappers as
        // if they were entities (which produced empty label/content).
        const arraySegments = getPathSegments(arrayPath);
        if (arraySegments.length === 1) {
            const collectionKey = arraySegments[0];
            lines.push('if isinstance(json_value, list):');
            lines.push('    merged = []');
            lines.push('    for item in json_value:');
            lines.push(`        if isinstance(item, dict) and "${collectionKey}" in item:`);
            lines.push(`            merged.extend(item.get("${collectionKey}") or [])`);
            lines.push('        else:');
            lines.push('            merged.append(item)');
            lines.push(`    json_value = {"${collectionKey}": merged}`);
            lines.push('');
        }
        lines.push('entities = []');
        lines.push(`for record in ${collectionAccessor}:`);
        lines.push('    entities.append({');
        lines.push(`        "label": ${relativeLabel},`);
        lines.push(`        "content": ${relativeContent},`);
        // ``or 0`` guards against entities that omit start_index (key missing or
        // explicitly null), which would otherwise raise ``int(None)``.
        lines.push(`        "start_index": int(${relativeStart} or 0),`);
        lines.push('    })');
        lines.push('return entities');
        return lines.join('\n');
    }

    return [
        ...JSON_FENCE_STRIP_LINES,
        'json_value = json.loads(llm_output_clean)',
        '',
        'return [{',
        `    "label": json_value${labelAccessor},`,
        `    "content": json_value${contentAccessor},`,
        `    "start_index": int(json_value${startAccessor} or 0),`,
        '}]',
    ].join('\n');
};

export const createNerWithRelationsPrefillCode = (entityPaths, relationPaths) => {
    const labelAccessor = buildJsonAccessor(entityPaths.labelPath, '""');
    const contentAccessor = buildJsonAccessor(entityPaths.contentPath, '""');
    const startAccessor = buildJsonAccessor(entityPaths.startIndexPath, '""');
    if (!labelAccessor || !contentAccessor || !startAccessor) {
        return null;
    }
    const relationLabelAccessor = buildJsonAccessor(relationPaths.labelPath, '""');
    const sourceAccessor = buildJsonAccessor(relationPaths.sourceIndexPath, '""');
    const targetAccessor = buildJsonAccessor(relationPaths.targetIndexPath, '""');
    if (!relationLabelAccessor || !sourceAccessor || !targetAccessor) {
        return null;
    }

    const entityArrayPath = getCommonArrayPath(
        entityPaths.labelArrayPath,
        entityPaths.contentArrayPath,
        entityPaths.startIndexArrayPath,
    );
    const relationArrayPath = getCommonArrayPath(
        relationPaths.labelArrayPath,
        relationPaths.sourceArrayPath,
        relationPaths.targetArrayPath,
    );

    const lines = [
        ...JSON_FENCE_STRIP_LINES,
        'json_value = json.loads(llm_output_clean)',
        '',
    ];

    if (entityArrayPath !== null) {
        const collectionAccessor = buildAccessorWithBase(entityArrayPath);
        const relativeLabel = buildRelativeAccessor(entityPaths.labelPath, entityArrayPath, 'entity');
        const relativeContent = buildRelativeAccessor(entityPaths.contentPath, entityArrayPath, 'entity');
        const relativeStart = buildRelativeAccessor(entityPaths.startIndexPath, entityArrayPath, 'entity');
        if (!relativeLabel || !relativeContent || !relativeStart) {
            return null;
        }
        lines.push('entities = []');
        lines.push(`for entity in ${collectionAccessor}:`);
        lines.push('    entities.append({');
        lines.push(`        "label": ${relativeLabel},`);
        lines.push(`        "content": ${relativeContent},`);
        lines.push(`        "start_index": int(${relativeStart} or 0),`);
        lines.push('    })');
    } else {
        lines.push('entities = [{');
        lines.push(`    "label": json_value${labelAccessor},`);
        lines.push(`    "content": json_value${contentAccessor},`);
        lines.push(`    "start_index": int(json_value${startAccessor} or 0),`);
        lines.push('}]');
    }

    if (relationArrayPath !== null) {
        const collectionAccessor = buildAccessorWithBase(relationArrayPath);
        const relativeLabel = buildRelativeAccessor(relationPaths.labelPath, relationArrayPath, 'relation');
        const relativeSource = buildRelativeAccessor(relationPaths.sourceIndexPath, relationArrayPath, 'relation');
        const relativeTarget = buildRelativeAccessor(relationPaths.targetIndexPath, relationArrayPath, 'relation');
        if (!relativeLabel || !relativeSource || !relativeTarget) {
            return null;
        }
        lines.push('');
        lines.push('relations = []');
        lines.push(`for relation in ${collectionAccessor}:`);
        lines.push('    relations.append({');
        lines.push(`        "label": ${relativeLabel},`);
        lines.push(`        "source_start_index": int(${relativeSource} or 0),`);
        lines.push(`        "target_start_index": int(${relativeTarget} or 0),`);
        lines.push('    })');
    } else {
        lines.push('');
        lines.push('relations = [{');
        lines.push(`    "label": json_value${relationLabelAccessor},`);
        lines.push(`    "source_start_index": int(json_value${sourceAccessor} or 0),`);
        lines.push(`    "target_start_index": int(json_value${targetAccessor} or 0),`);
        lines.push('}]');
    }

    lines.push('');
    lines.push('return {');
    lines.push('    "entities": entities,');
    lines.push('    "relations": relations,');
    lines.push('}');

    return lines.join('\n');
};

export const createSummarizationPrefillCode = (prefill) => {
    if (prefill?.type === 'text') {
        // For text response, return the text directly
        return [
            'return {',
            '    "response": (llm_output or "").strip(),',
            '}',
        ].join('\n');
    }

    // JSON mode with no mapped field (content generation): return the whole
    // fence-stripped output so dict/exact match compare the entire object.
    // No json.loads on purpose — a non-JSON reply still stores cleanly.
    if (prefill?.type === 'json' && !prefill?.fieldPath) {
        return [
            ...JSON_FENCE_STRIP_LINES,
            '',
            'return {',
            '    "response": llm_output_clean,',
            '}',
        ].join('\n');
    }

    // For JSON response, extract the response field
    if (!prefill?.fieldPath) return null;
    const accessor = buildJsonAccessor(prefill.fieldPath, '""');
    if (!accessor) return null;

    return [
        ...JSON_FENCE_STRIP_LINES,
        'json_value = json.loads(llm_output_clean)',
        '',
        'return {',
        `    "response": str(json_value${accessor}).strip(),`,
        '}',
    ].join('\n');
};

export const generateInitialParserCode = (taskType, parserPrefill) => {
    if (!taskType || !parserPrefill) {
        return null;
    }
    if (taskType === TaskType.classification) {
        const classificationPrefill = parserPrefill.classification;
        if (!classificationPrefill) return null;
        return createClassificationPrefillCode(classificationPrefill);
    }
    if (taskType === TaskType.NER) {
        if (!parserPrefill.ner) return null;
        return createNerPrefillCode(parserPrefill.ner);
    }
    if (taskType === TaskType.NER_WITH_RELATIONS) {
        if (!parserPrefill.ner || !parserPrefill.relations) return null;
        return createNerWithRelationsPrefillCode(parserPrefill.ner, parserPrefill.relations);
    }
    if (isGenerativeTask(taskType)) {
        const summarizationPrefill = parserPrefill.summarization;
        if (!summarizationPrefill) return null;
        return createSummarizationPrefillCode(summarizationPrefill);
    }
    return null;
};
